/**
 * @prettier
 */
import {createSelector} from 'reselect';
import {getTranslations, getContactOwnerForm, getTravelerFeatures} from './store';
import {isInquiryModalOpen} from './modalStates';
import {getHostType} from './listing';
import {getUserData} from './userData';
import {labelMaker} from '../../server/utils/labelMaker';

const getInquiryModalPlatformLabels = createSelector(
    [getTranslations, getHostType],
    (trans, hostType) => {
        let labels = {};
        if (hostType.owner) {
            labels = {
                question: trans.questionForOwner,
                message: trans.messageToOwner
            };
        } else {
            labels = {
                question: trans.questionForManager,
                message: trans.messageToManager
            };
        }
        return labels;
    }
);

/* eslint-disable complexity */
const getContactOwnerFormLabels = createSelector(
    [getTranslations, getContactOwnerForm, getInquiryModalPlatformLabels, getTravelerFeatures],
    (trans, contactOwnerForm, inquiryModalPlatformLabels, travelerFeatures) => {
        const {question, message} = inquiryModalPlatformLabels;
        const {invalid, isFormValid, touched} = contactOwnerForm;

        const showFirstNameErrorMsg =
            !isFormValid && touched.has('firstName') && invalid.has('firstName');
        const showLastNameErrorMsg =
            !isFormValid && touched.has('lastName') && invalid.has('lastName');
        const showEmailAddressErrorMsg =
            !isFormValid && touched.has('emailAddress') && invalid.has('emailAddress');
        const showTermsAndConditionsErrorMsg =
            !isFormValid &&
            touched.has('termsAndConditionsAgreed') &&
            invalid.has('termsAndConditionsAgreed');

        return {
            firstName: trans.firstName,
            lastName: trans.lastName,
            emailAddress: trans.emailAddress,
            message,
            firstNameErrorMsg: showFirstNameErrorMsg ? trans.firstNameErrorMsg : '',
            lastNameErrorMsg: showLastNameErrorMsg ? trans.lastNameErrorMsg : '',
            emailAddressErrorMsg: showEmailAddressErrorMsg ? trans.emailAddressErrorMsg : '',
            termsAndConditionsAgreedErrorMsg: showTermsAndConditionsErrorMsg
                ? trans.termsAndConditionsAgreedErrorMsg
                : '',
            phoneNumber: travelerFeatures.inquiryPhoneIsMobile
                ? trans.mobileNumber
                : trans.phoneNumber,
            question,
            countryCodes: trans.countryCodes,
            inquirySubscribeText: trans.inquirySubscribeText,
            inquiryTermsAgreementText: labelMaker(trans.inquiryTermsAgreementText, trans.send)
        };
    }
);
/* eslint-enable complexity */

const getContactOwnerFormHasTripDetailsErrors = createSelector(
    [getTranslations, getContactOwnerForm, isInquiryModalOpen],
    (trans, contactOwnerForm, inquiryModalOpen) => {
        const hasInvalidGuests = !!contactOwnerForm.invalid.has('guests');

        return inquiryModalOpen ? hasInvalidGuests : null;
    }
);

const getContactOwnerFormDetails = createSelector(
    [
        getTranslations,
        getContactOwnerForm,
        getContactOwnerFormLabels,
        getTravelerFeatures,
        getUserData
    ],
    (trans, contactOwnerForm, contactOwnerFormLabels, travelerFeatures, userData) => {
        const {
            firstName,
            lastName,
            countryCode,
            countryName,
            phoneNumber,
            emailAddress,
            message,
            termsAndConditionsAgreed,
            newsSubscribed,
            isFormValid
        } = contactOwnerForm;
        const {termsAndConditionsRequired, showNewsCheckbox} = travelerFeatures;

        return {
            labels: contactOwnerFormLabels,
            firstName,
            lastName,
            countryCode,
            countryName,
            phoneNumber,
            emailAddress,
            message,
            termsAndConditionsAgreed,
            termsAndConditionsRequired,
            newsSubscribed,
            showNewsCheckbox,
            isFormValid,
            userData
        };
    }
);

export default getContactOwnerFormDetails;
export {
    getInquiryModalPlatformLabels,
    getContactOwnerFormLabels,
    getContactOwnerFormDetails,
    getContactOwnerFormHasTripDetailsErrors
};
